import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Globe, Languages, ArrowRight, Loader2, Copy, CheckCircle,
  RefreshCw, BookOpen, Volume2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Language flag emojis (using text representations for compatibility)
const languageFlags = {
  english: "EN",
  mandarin: "中",
  spanish: "ES",
  french: "FR",
  german: "DE",
  japanese: "日",
  korean: "한",
  portuguese: "PT",
  italian: "IT",
  russian: "RU",
  arabic: "ع",
  hindi: "हि"
};

const TranslationSection = ({ manuscript, onTranslationComplete }) => {
  const [languages, setLanguages] = useState([]);
  const [sourceLanguage, setSourceLanguage] = useState('english');
  const [targetLanguage, setTargetLanguage] = useState('mandarin');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      const response = await axios.get(`${API}/translate/languages`);
      setLanguages(response.data.languages);
    } catch (err) {
      console.error('Error loading languages:', err);
    }
  };

  const translateText = async () => {
    if (!manuscript || manuscript.length < 10) {
      setError('Please add some text to your manuscript first');
      return;
    }

    setIsTranslating(true);
    setError(null);
    setTranslatedText('');

    try {
      const response = await axios.post(`${API}/translate`, {
        text: manuscript,
        source_language: sourceLanguage,
        target_language: targetLanguage,
        preserve_formatting: true
      });

      setTranslatedText(response.data.translated);
      
      if (onTranslationComplete) {
        onTranslationComplete(response.data.translated, targetLanguage);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Translation failed. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const swapLanguages = () => {
    const temp = sourceLanguage;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(temp);
    setTranslatedText('');
  };

  const copyTranslation = () => {
    navigator.clipboard.writeText(translatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  const getLanguageNative = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.native : code;
  };

  return (
    <div className="translation-section" data-testid="translation-section">
      <div className="translation-header">
        <h4><Languages size={18} /> Translation Studio</h4>
        <p className="section-description">
          Translate your manuscript to reach international audiences. 
          Perfect for publishing in multiple markets.
        </p>
      </div>

      {/* Language Selector */}
      <div className="language-selector">
        <div className="language-select-group">
          <label>From</label>
          <select 
            value={sourceLanguage} 
            onChange={(e) => setSourceLanguage(e.target.value)}
            className="language-select"
            data-testid="source-language-select"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {languageFlags[lang.code]} {lang.name} ({lang.native})
              </option>
            ))}
          </select>
        </div>

        <button 
          className="swap-btn" 
          onClick={swapLanguages}
          title="Swap languages"
          data-testid="swap-languages-btn"
        >
          <RefreshCw size={18} />
        </button>

        <div className="language-select-group">
          <label>To</label>
          <select 
            value={targetLanguage} 
            onChange={(e) => setTargetLanguage(e.target.value)}
            className="language-select"
            data-testid="target-language-select"
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {languageFlags[lang.code]} {lang.name} ({lang.native})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Quick Select Popular Pairs */}
      <div className="popular-pairs">
        <span className="pairs-label">Quick select:</span>
        <button 
          className={`pair-btn ${sourceLanguage === 'english' && targetLanguage === 'mandarin' ? 'active' : ''}`}
          onClick={() => { setSourceLanguage('english'); setTargetLanguage('mandarin'); }}
        >
          EN → 中文
        </button>
        <button 
          className={`pair-btn ${sourceLanguage === 'english' && targetLanguage === 'spanish' ? 'active' : ''}`}
          onClick={() => { setSourceLanguage('english'); setTargetLanguage('spanish'); }}
        >
          EN → ES
        </button>
        <button 
          className={`pair-btn ${sourceLanguage === 'mandarin' && targetLanguage === 'english' ? 'active' : ''}`}
          onClick={() => { setSourceLanguage('mandarin'); setTargetLanguage('english'); }}
        >
          中文 → EN
        </button>
      </div>

      {/* Translate Button */}
      <button 
        className="primary-btn translate-btn"
        onClick={translateText}
        disabled={isTranslating || !manuscript}
        data-testid="translate-btn"
      >
        {isTranslating ? (
          <>
            <Loader2 className="spinning" size={18} />
            <span>Translating...</span>
          </>
        ) : (
          <>
            <Globe size={18} />
            <span>Translate to {getLanguageNative(targetLanguage)}</span>
          </>
        )}
      </button>

      {error && (
        <div className="translation-error">
          <span>{error}</span>
        </div>
      )}

      {/* Translation Result */}
      {translatedText && (
        <div className="translation-result" data-testid="translation-result">
          <div className="result-header">
            <h5>
              <CheckCircle size={16} /> 
              Translated to {getLanguageName(targetLanguage)}
            </h5>
            <button 
              className="copy-btn"
              onClick={copyTranslation}
              data-testid="copy-translation-btn"
            >
              {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="translated-content">
            {translatedText}
          </div>
          <div className="result-stats">
            <span>Original: {manuscript?.length?.toLocaleString()} chars</span>
            <span>Translated: {translatedText.length.toLocaleString()} chars</span>
          </div>
          <div className="result-actions">
            <p className="tip">
              <BookOpen size={14} />
              <span>You can now use this translated text in the Audiobook section to create a {getLanguageName(targetLanguage)} audiobook!</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationSection;
